import http from "@/api/request";
import { SYSTEM_OPS_BASE, USERS_AUTH_BASE } from "@/config/apiBaseUrlConfig";

import type {
  CaptchaVO,
  CurrentUserVO,
  GetUserMenuTreeParams,
  GetWechatQrParams,
  GetWechatTicketStatusParams,
  LoginPasswordRequest,
  LoginResultVO,
  LoginSmsRequest,
  OAuthQrPrepareVO,
  OPSUserMenuPermissionVO,
  SendSmsLoginCodeRequest,
  WeChatQrLoginRequest,
  WechatTicketStatusVO,
} from "./type";

/** 运营端权限模块 API 前缀（ops） */
const OPS_PERMISSION_BASE = `${SYSTEM_OPS_BASE}/permission`;

/**
 * 账号密码登录（含图形验证码）
 * POST /service-chijing-users/api/auth/userLogin
 * @param data 登录账号、密码、验证码与系统端
 */
export const userLogin = (data: LoginPasswordRequest) => {
  return http.post<LoginResultVO>(`${USERS_AUTH_BASE}/userLogin`, data);
};

/**
 * 手机号短信验证码登录
 * POST /service-chijing-users/api/auth/userLoginBySms
 * @param data 手机号、短信验证码与系统端
 */
export const userLoginBySms = (data: LoginSmsRequest) => {
  return http.post<LoginResultVO>(`${USERS_AUTH_BASE}/userLoginBySms`, data);
};

/**
 * 发送短信登录验证码
 * POST /service-chijing-users/api/auth/sendSms
 * @param data 接收短信的手机号
 */
export const sendSmsLoginCode = (data: SendSmsLoginCodeRequest) => {
  return http.post<void>(`${USERS_AUTH_BASE}/sendSms`, data);
};

/**
 * 获取当前登录账号信息
 * GET /service-chijing-users/api/auth/getCurrentUser
 */
export const getCurrentUser = () => {
  return http.get<CurrentUserVO>(`${USERS_AUTH_BASE}/getCurrentUser`);
};

/**
 * 获取图形验证码
 * GET /service-chijing-users/api/auth/captcha
 */
export const getCaptcha = () => {
  return http.get<CaptchaVO>(`${USERS_AUTH_BASE}/captcha`);
};

/**
 * 微信扫码登录：获取授权页 URL 与 state
 * GET /service-chijing-users/api/auth/getWechatQr
 * @param params.systemPoint 登录系统端标识，如 ops
 */
export const getWechatQr = (params: GetWechatQrParams) => {
  return http.get<OAuthQrPrepareVO>(`${USERS_AUTH_BASE}/getWechatQr`, { params });
};

/**
 * 微信扫码登录：code + state 换 JWT
 * POST /service-chijing-users/api/auth/wechatQrLogin
 * @param data 微信回调 code 与 getWechatQr 返回的 state
 */
export const wechatQrLogin = (data: WeChatQrLoginRequest) => {
  return http.post<LoginResultVO>(`${USERS_AUTH_BASE}/wechatQrLogin`, data);
};

/**
 * 微信扫码登录：轮询 ticket 状态（已废弃，请使用 WebSocket）
 * GET /service-chijing-users/api/auth/getWechatTicketStatus
 * @param params.ticket 与 getWechatQr 返回的 wsTicket 一致
 */
export const getWechatTicketStatus = (params: GetWechatTicketStatusParams) => {
  return http.get<WechatTicketStatusVO>(`${USERS_AUTH_BASE}/getWechatTicketStatus`, { params });
};

/**
 * 当前用户菜单权限：OPS 登录后拉取侧边栏菜单与按钮权限码
 * GET /service-chijing-system/api/ops/permission/getUserMenuTree
 * @param params.systemPoint 系统端标识，须与 JWT 登录端一致，如 ops
 */
export const getUserMenuTree = (params: GetUserMenuTreeParams) => {
  return http.get<OPSUserMenuPermissionVO>(`${OPS_PERMISSION_BASE}/getUserMenuTree`, { params });
};
